import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
} from "@mui/material";
import { useCreateConnectorConfigurationMutation } from "ConnectorConfigurations/Api/Mutations/useCreateConnectorConfigurationMutation";
import { useUpdateConnectorConfigurationMutation } from "ConnectorConfigurations/Api/Mutations/useUpdateConnectorConfigurationMutation";
import type { ConnectorConfigurationListItemDto } from "Infrastructure/Api/Api";
import { type FC, useEffect, useState } from "react";

type Props = {
	open: boolean;
	onClose: () => void;
	editData?: ConnectorConfigurationListItemDto;
};

export const CreateConnectorConfigurationDialog: FC<Props> = ({
	open,
	onClose,
	editData,
}) => {
	const [provider, setProvider] = useState("");
	const [endpoint, setEndpoint] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [errors, setErrors] = useState({
		provider: "",
		endpoint: "",
		apiKey: "",
	});
	const createMutation = useCreateConnectorConfigurationMutation();
	const updateMutation = useUpdateConnectorConfigurationMutation();

	const isEditMode = !!editData;

	useEffect(() => {
		if (editData) {
			setProvider(editData.provider);
			setEndpoint(editData.endpoint);
			setApiKey("");
		} else {
			setProvider("");
			setEndpoint("");
			setApiKey("");
		}
		setErrors({ provider: "", endpoint: "", apiKey: "" });
	}, [editData, open]);

	const handleClose = () => {
		setProvider("");
		setEndpoint("");
		setApiKey("");
		setErrors({ provider: "", endpoint: "", apiKey: "" });
		onClose();
	};

	const handleSubmit = async () => {
		// Validation
		const newErrors = {
			provider: !provider.trim() ? "Provider is required" : "",
			endpoint: !endpoint.trim() ? "Endpoint is required" : "",
			apiKey: !isEditMode && !apiKey.trim() ? "API Key is required" : "",
		};

		setErrors(newErrors);

		if (Object.values(newErrors).some((error) => error)) {
			return;
		}

		try {
			if (isEditMode && editData) {
				await updateMutation.mutateAsync({
					id: editData.connectorConfigurationID,
					command: {
						provider: provider.trim(),
						endpoint: endpoint.trim(),
						// Empty means "keep the existing key" - the API never hands it back
						apiKey: apiKey.trim(),
					},
				});
			} else {
				await createMutation.mutateAsync({
					provider: provider.trim(),
					endpoint: endpoint.trim(),
					apiKey: apiKey.trim(),
				});
			}
			handleClose();
		} catch (err) {
			setErrors({
				...errors,
				provider: `Failed to ${isEditMode ? "update" : "create"} connector configuration. Please try again.`,
			});
		}
	};

	const isLoading = createMutation.isPending || updateMutation.isPending;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{isEditMode
					? "Edit Connector Configuration"
					: "Create Connector Configuration"}
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						autoFocus
						label="Provider"
						type="text"
						fullWidth
						variant="outlined"
						value={provider}
						onChange={(e) => {
							setProvider(e.target.value);
							setErrors({ ...errors, provider: "" });
						}}
						error={!!errors.provider}
						helperText={errors.provider}
						placeholder="Enter provider (e.g., OpenAI, Anthropic)"
					/>
					<TextField
						label="Endpoint"
						type="text"
						fullWidth
						variant="outlined"
						value={endpoint}
						onChange={(e) => {
							setEndpoint(e.target.value);
							setErrors({ ...errors, endpoint: "" });
						}}
						error={!!errors.endpoint}
						helperText={errors.endpoint}
						placeholder="Enter API endpoint URL"
					/>
					<TextField
						label="API Key"
						type="password"
						fullWidth
						variant="outlined"
						value={apiKey}
						onChange={(e) => {
							setApiKey(e.target.value);
							setErrors({ ...errors, apiKey: "" });
						}}
						error={!!errors.apiKey}
						helperText={
							errors.apiKey ||
							(isEditMode ? "Leave blank to keep existing key" : "")
						}
						placeholder={
							isEditMode ? "Enter new API key (optional)" : "Enter API key"
						}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="inherit">
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					disabled={isLoading}
				>
					{isLoading
						? isEditMode
							? "Updating..."
							: "Creating..."
						: isEditMode
							? "Update"
							: "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
